import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Spin, Button, Modal, Tooltip, Tag, Popover } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import ArticleCreationForm from "../../Forms/ArticleCreationForm/ArticleCreationForm";
import { getDepts } from "../../../../Services/DeptService";
import { getColors } from "../../../../Services/ColorService";

const ArticleTable = ({
  history,
  loading,
  data,
  setSelectedCategory,
  callReFetch,
  categoryData,
  userData,
  brandData,
  dispatch,
  handleDelete,
  subCategoryData,
  callReFetchSubCategory,
  principleData,
  divisionData,
  typeData,
  pagination,
  onPaginationChange,
}) => {
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [deptData, setDeptData] = useState([]);
  const [colorData, setColorData] = useState([]);

  // Update isMobileView based on window size
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Fetch department data
  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const response = await getDepts();
        if (response && response.data) {
          setDeptData(response.data);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchDepts();
  }, []);

  // Fetch color data
  useEffect(() => {
    const fetchColors = async () => {
      try {
        const response = await getColors();
        if (response && response.data) {
          setColorData(response.data);
        }
      } catch (error) {
        console.error("Error fetching colors:", error);
      }
    };

    fetchColors();
  }, []);

  const getPrincipleName = (principleId) => {
    const principle = principleData.find((p) => p._id === principleId);
    return principle ? principle.name : "-";
  };

  // Calculate preSKU for an article
  const calculatePreSKU = (article) => {
    if (article.brand && article.preBarcode && article.code) {
      // Get first 3 characters from brand name
      const brandNameFirst3 = article.brand.substring(0, 3);
      return `${brandNameFirst3}${article.preBarcode}${article.code}`;
    }
    return "-";
  };

  const handleOpenEditModal = async (id) => {
    const articleToEdit = data.find((article) => article._id === id);
    if (articleToEdit) {
      // Make a deep copy of the article to avoid modifying the original data
      const preparedArticle = JSON.parse(JSON.stringify(articleToEdit));

      // Ensure prices array is properly formatted for the form
      if (preparedArticle.prices && preparedArticle.prices.length > 0) {
        preparedArticle.prices = preparedArticle.prices.map((price) => ({
          ...price,
          // Set applyToAllColors flag based on colorId being null
          applyToAllColors: price.colorId === null,
        }));
      }

      setSelectedArticle(preparedArticle);
      setIsEditModalVisible(true);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setSelectedArticle(null);
  };

  // Helper function to get department name from ID
  const getDeptName = (deptId) => {
    const dept = deptData.find((d) => d._id === deptId);
    return dept ? dept.name : "Unknown";
  };

  // Helper function to get color name from ID
  const getColorName = (colorId) => {
    if (colorId === null) {
      return "All Colors";
    }
    const color = colorData.find((c) => c._id === colorId);
    return color ? color.name : "Unknown";
  };

  // Helper function to render price details in a popover
  const renderPriceDetails = (prices) => {
    if (!prices || prices.length === 0) {
      return <span>No prices available</span>;
    }

    return (
      <div style={{ maxHeight: "300px", overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>
                Department
              </th>
              <th style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>
                Color
              </th>
              <th style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>
                Price
              </th>
            </tr>
          </thead>
          <tbody>
            {prices.map((price, index) => (
              <tr key={index}>
                <td
                  style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}
                >
                  {getDeptName(price.deptId)}
                </td>
                <td
                  style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}
                >
                  {getColorName(price.colorId)}
                </td>
                <td
                  style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}
                >
                  Rp. {Number(price.harga_internal).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const isAdmin = userData?.roles?.includes("admin");
  const renderActionButton = (id) => {
    if (!isAdmin) return null;
    return (
      <div style={{ display: "flex", gap: "8px" }}>
        <Tooltip title="Edit">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleOpenEditModal(id)}
            type="primary"
            ghost
          />
        </Tooltip>
        <Tooltip title="Delete">
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(id)}
            danger
            ghost
          />
        </Tooltip>
      </div>
    );
  };

  const handleTableOnChange = (tablePagination, filteredInfo, sorter) => {
    // Handle pagination change
    if (onPaginationChange && tablePagination) {
      onPaginationChange(tablePagination);
    }

    // Handle filters and sorters
    dispatch({ type: "sorter", payload: sorter });
    dispatch({ type: "filteredInfo", payload: filteredInfo });
    callReFetch();
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
    },
      {
        title: "PreBarcode",
        key: "preBarcode",
        render: (_, record) => {
          if (record.preBarcode && record.code) {
            return `${record.preBarcode}${record.code}`;
          }
          return "-";
        },
        sorter: (a, b) => {
          const preBarcodeA = a.preBarcode && a.code ? `${a.preBarcode}${a.code}` : "";
          const preBarcodeB = b.preBarcode && b.code ? `${b.preBarcode}${b.code}` : "";
          return preBarcodeA.localeCompare(preBarcodeB);
        },
      },
    {
      title: "PreSKU",
      key: "preSKU",
      render: (_, record) => {
        const preSKU = calculatePreSKU(record);
        return (
          <span style={{ 
            fontFamily: "monospace", 
            fontSize: "12px",
            backgroundColor: "#f5f5f5",
            padding: "2px 6px",
            borderRadius: "4px",
            color: "#666"
          }}>
            {preSKU}
          </span>
        );
      },
      sorter: (a, b) => {
        const preSKUA = calculatePreSKU(a);
        const preSKUB = calculatePreSKU(b);
        return preSKUA.localeCompare(preSKUB);
      },
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      sorter: true,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      sorter: true,
    },
    {
      title: "SubCategory",
      dataIndex: "subcategory",
      key: "subcategory",
      sorter: true,
    },
    {
      title: "Division",
      dataIndex: "division",
      key: "division",
      sorter: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
    },
    {
      title: "HPP",
      dataIndex: "hpp",
      key: "hpp",
      render: (hpp) => {
        return hpp !== undefined ? `Rp. ${Number(hpp).toLocaleString()}` : "-";
      },
      sorter: true,
    },
    {
      title: "Principle",
      dataIndex: "principleId",
      key: "principleId",
      render: (principleId) => getPrincipleName(principleId),
      sorter: (a, b) => {
        const nameA = getPrincipleName(a.principleId).toLowerCase();
        const nameB = getPrincipleName(b.principleId).toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Prices",
      key: "prices",
      render: (record) => {
        const priceCount = record.prices?.length || 0;
        return (
          <Popover
            content={renderPriceDetails(record.prices)}
            title="Price Details"
            trigger="click"
            placement="right"
          >
            <Button type="link" icon={<DollarOutlined />}>
              {priceCount} {priceCount === 1 ? "price" : "prices"}
            </Button>
          </Popover>
        );
      },
    },
    {
      title: "Release Date",
      dataIndex: "released_date",
      key: "released_date",
      render: (date) => {
        if (!date) return "-";
        const d = new Date(date);
        return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${d.getFullYear()}`;
      },
      sorter: true,
    },
    {
      title: "Age",
      key: "age",
      render: (_, record) => {
        if (!record.released_date) return "-";
        const releaseDate = new Date(record.released_date);
        const today = new Date();
        const diffTime = Math.abs(today - releaseDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `${diffDays} days`;
      },
      sorter: (a, b) => {
        if (!a.released_date || !b.released_date) return 0;
        const dateA = new Date(a.released_date);
        const dateB = new Date(b.released_date);
        return dateA - dateB;
      },
    },
    {
      title: "Action",
      render: (item) => renderActionButton(item._id),
      width: 200,
    },
  ];

  return (
    <>
      <div style={{ 
        width: "100vw", 
        maxWidth: "100%",
        overflowX: "auto",
        padding: "0 16px",
        boxSizing: "border-box"
      }}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={Array.isArray(data) ? data : []}
            onChange={handleTableOnChange}
            pagination={pagination}
            rowKey="_id"
            scroll={{ x: "max-content" }}
            size="small"
          />
        </Spin>
        <Modal
          title="Edit Article"
          open={isEditModalVisible}
          footer={null}
          onCancel={handleCloseEditModal}
        >
          <ArticleCreationForm
            history={history}
            isEditMode
            callReFetch={callReFetch}
            categoryData={categoryData}
            brandData={brandData}
            data={selectedArticle}
            setSelectedCategory={setSelectedCategory}
            subCategoryData={subCategoryData}
            callReFetchSubCategory={callReFetchSubCategory}
            divisionData={divisionData}
            principleData={principleData}
            typeData={typeData}
            onClose={handleCloseEditModal}
            userData={userData}
          />
        </Modal>
      </div>
    </>
  );
};

ArticleTable.propTypes = {};

export default ArticleTable;
